/* eslint-disable react-hooks/exhaustive-deps */

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReloadIcon from "@mui/icons-material/Replay";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  capitalize,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchAuthors } from "../../app/slices/authorSlice";
import { fetchGenres } from "../../app/slices/commonSlice";
import { journalCategories, uploadTypes } from "../../app/utils";
import FileInput from "../../components/fileInput/fileInput";

const ManageUploads = ({
  formValues,
  handleChange,
  errors = {},
  data = null,
  setFormValues,
  requiredFields = [],
}) => {
  const dispatch = useDispatch();
  const { list: authors } = useSelector(({ authors }) => authors.get);
  const { list: genres } = useSelector(({ common }) => common.genres);

  const [expanded, setExpanded] = useState("optional");

  const handleExpand = (panel) => (_, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  useEffect(() => {
    dispatch(fetchAuthors());
    dispatch(fetchGenres({ resource: "genres" }));
  }, []);

  const allFields = [
    "type",
    "category",
    "featured",
    "genre",
    "title_en",
    "title_sw",
    "subTitle_en",
    "subTitle_sw",
    "authorId",
    "edition",
    "publisher",
    "placeOfPublication",
    "publicationDate",
    "publicationYear",
    "isbn",
    "issn",
    "series",
    "volumeIssue",
    "subjects_en",
    "subjects_sw",
    "summary_en",
    "summary_sw",
    "duration",
    "coverImage",
    "document",
    "documentSW",
  ];

  const requiredFieldsFinal = useMemo(() => {
    const base = requiredFields?.filter((f) => f !== "type") || [];
    return ["type", ...base];
  }, [requiredFields]);

  const optionalFields = useMemo(() => {
    return allFields.filter((field) => !requiredFieldsFinal.includes(field));
  }, [requiredFieldsFinal]);

  useEffect(() => {
    if (!data) return;

    const updatedPayload = {
      title_en: data?.TITLE_EN,
      title_sw: data?.TITLE_SW,
      subTitle_en: data?.SUB_TITLE_EN,
      subTitle_sw: data?.SUB_TITLE_SW,
      authorId: data?.AUTHOR_ID,
      edition: data?.EDITION,
      category: data?.CATEGORY,
      featured: data?.FEATURED,
      publisher: data?.PUBLISHER,
      placeOfPublication: data?.PLACE_OF_PUBLICATION,
      publicationDate: data?.PUBLICATION_DATE,
      publicationYear: data?.PUBLICATION_YEAR,
      type: data?.TYPE,
      isbn: data?.ISBN,
      issn: data?.ISSN,
      genre: data?.GENRE_ID,
      subjects_en: data?.SUBJECTS_EN,
      subjects_sw: data?.SUBJECTS_SW,
      series: data?.SERIES,
      volumeIssue: data?.VOLUME_ISSUE,
      coverImage: data?.COVER_IMAGE,
      duration: data?.DURATION,
      summary_en: data?.SUMMARY_EN,
      summary_sw: data?.SUMMARY_SW,
    };

    setFormValues((prev) => ({ ...prev, ...updatedPayload }));
  }, [data]);

  const generateYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from(
      { length: currentYear - 1899 },
      (_, i) => currentYear - i,
    );
  }, []);

  const fieldLabels = {
    type: "Upload Type",
    category: "Journal Category",
    featured: "Featured",
    genre: "Genre",
    title_en: "Title (English)",
    title_sw: "Title (Swahili)",
    subTitle_en: "Subtitle (English)",
    subTitle_sw: "Subtitle (Swahili)",
    authorId: "Author",
    edition: "Edition",
    publisher: "Publisher",
    placeOfPublication: "Place of Publication",
    publicationDate: "Publication Date",
    publicationYear: "Publication Year",
    isbn: "ISBN",
    issn: "ISSN",
    series: "Series",
    volumeIssue: "Volume / Issue",
    subjects_en: "Subjects (English)",
    subjects_sw: "Subjects (Swahili)",
    summary_en: "Summary (English)",
    summary_sw: "Summary (Swahili)",
    duration: "Duration (Minutes)",
    coverImage: "Cover Image",
    document: "File",
    documentSW: "File (Swahili)",
  };

  const handleFileType = useMemo(() => {
    const fileTypeMap = {
      audioBook: "audio/*",
      eBook: "application/pdf,application/epub+zip",
      default:
        "application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword",
    };

    return fileTypeMap[formValues?.type] || fileTypeMap.default;
  }, [formValues?.type]);

  const fieldConfig = {
    type: {
      render: () => (
        <Autocomplete
          options={uploadTypes}
          getOptionLabel={(option) => capitalize(option.label)}
          value={
            uploadTypes.find((opt) => opt.value === formValues?.type) || null
          }
          onChange={(e, value) =>
            handleChange({
              target: { name: "type", value: value?.value || "" },
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Upload Type"
              margin="dense"
              error={Boolean(errors?.type)}
              helperText={errors?.type?.[0]}
            />
          )}
        />
      ),
    },

    category: {
      render: () =>
        formValues?.type === "journal" && (
          <Autocomplete
            options={journalCategories}
            value={formValues?.category || null}
            onChange={(e, value) =>
              handleChange({
                target: { name: "category", value: value || "" },
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Journal Category"
                margin="dense"
                error={Boolean(errors?.category)}
                helperText={errors?.category?.[0]}
              />
            )}
          />
        ),
    },

    featured: {
      render: () =>
        formValues?.type === "eBook" && (
          <Autocomplete
            options={[
              { label: "No", value: "N" },
              { label: "Yes", value: "Y" },
            ]}
            getOptionLabel={(option) => option.label}
            value={
              [
                { label: "No", value: "N" },
                { label: "Yes", value: "Y" },
              ].find((opt) => opt.value === formValues?.featured) || null
            }
            onChange={(e, value) =>
              handleChange({
                target: { name: "featured", value: value?.value || "" },
              })
            }
            renderInput={(params) => (
              <TextField {...params} label="Is Featured?" margin="dense" />
            )}
          />
        ),
    },

    genre: {
      render: () =>
        formValues?.type === "eBook" && (
          <Autocomplete
            options={genres || []}
            getOptionLabel={(option) => option.TITLE}
            value={
              genres?.find((g) => g.GENRE_ID === formValues?.genre) || null
            }
            onChange={(e, value) =>
              handleChange({
                target: { name: "genre", value: value?.GENRE_ID },
              })
            }
            renderInput={(params) => (
              <TextField {...params} label="Genre" margin="dense" />
            )}
            noOptionsText={
              <div style={{ display: "flex", alignItems: "center" }}>
                <span style={{ flex: 1, fontSize: ".8rem" }}>No options</span>
                <IconButton
                  size="small"
                  onClick={() => dispatch(fetchGenres({ resource: "genres" }))}
                >
                  <ReloadIcon fontSize="small" />
                </IconButton>
              </div>
            }
          />
        ),
    },

    authorId: {
      render: () => (
        <Autocomplete
          options={authors || []}
          getOptionLabel={(option) => option.NAME}
          value={
            authors?.find((a) => a.AUTHOR_ID === formValues?.authorId) || null
          }
          onChange={(e, value) =>
            handleChange({
              target: { name: "authorId", value: value?.AUTHOR_ID || "" },
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Author"
              margin="dense"
              error={Boolean(errors?.authorId)}
              helperText={errors?.authorId?.[0]}
            />
          )}
        />
      ),
    },

    publicationDate: {
      render: () => (
        <TextField
          type="date"
          fullWidth
          margin="dense"
          label="Publication Date"
          name="publicationDate"
          value={formValues?.publicationDate || ""}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            inputProps: { max: moment().format("YYYY-MM-DD") },
          }}
        />
      ),
    },

    publicationYear: {
      render: () => (
        <Autocomplete
          options={generateYears}
          value={formValues?.publicationYear || null}
          onChange={(e, value) =>
            handleChange({
              target: { name: "publicationYear", value: value || "" },
            })
          }
          renderInput={(params) => (
            <TextField {...params} label="Publication Year" margin="dense" />
          )}
        />
      ),
    },

    isbn: {
      render: () => (
        <TextField
          fullWidth
          margin="dense"
          label={fieldLabels.isbn}
          name="isbn"
          value={formValues?.isbn || ""}
          onChange={handleChange}
          error={Boolean(errors?.isbn)}
          helperText={errors?.isbn?.[0]}
          inputProps={{ maxLength: 13, minLength: 13 }}
        />
      ),
    },

    coverImage: {
      render: () => (
        <FileInput
          onChange={handleChange}
          file={formValues?.newCoverImage}
          fileType="image/*"
          label="Cover Image"
          name="newCoverImage"
        />
      ),
    },

    document: {
      render: () => (
        <FileInput
          onChange={handleChange}
          file={formValues?.document}
          fileType={handleFileType}
          label="File"
          name="document"
        />
      ),
    },

    documentSW: {
      render: () => (
        <FileInput
          onChange={handleChange}
          file={formValues?.documentSW}
          fileType={handleFileType}
          label="File (Swahili)"
          name="documentSW"
        />
      ),
    },
  };

  const renderField = (field) => {
    const config = fieldConfig[field];

    if (config?.render) return config.render();

    return (
      <TextField
        fullWidth
        margin="dense"
        label={fieldLabels[field]}
        name={field}
        value={formValues?.[field] || ""}
        onChange={handleChange}
        error={Boolean(errors?.[field])}
        helperText={errors?.[field]?.[0]}
      />
    );
  };

  return (
    <Grid container spacing={2}>
      {/* REQUIRED */}
      <Grid size={12}>
        <Accordion expanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>Required Information</Typography>
          </AccordionSummary>

          <AccordionDetails>
            <Grid container spacing={2}>
              {requiredFieldsFinal.map((field, index) => {
                const element = renderField(field);
                if (!element) return null;

                return (
                  <Grid
                    key={field}
                    size={
                      field === "type"
                        ? { xs: 12, sm: 6, md: 4 }
                        : { xs: 12, sm: 6, md: 4 }
                    }
                  >
                    {element}
                  </Grid>
                );
              })}
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>

      {/* OPTIONAL */}
      <Grid size={12}>
        <Accordion
          expanded={expanded === "optional"}
          onChange={handleExpand("optional")}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>Optional Information</Typography>
          </AccordionSummary>

          <AccordionDetails>
            <Grid container spacing={2}>
              {optionalFields.map((field) => {
                const element = renderField(field);
                if (!element) return null;

                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={field}>
                    {element}
                  </Grid>
                );
              })}
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>
    </Grid>
  );
};

export default ManageUploads;
